import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useIsSmallViewport } from '@/hooks/useIsSmallViewport';
import { useIsMobile } from '@/hooks/use-mobile';
import { useOwnsScreen } from '@/contexts/ScreenOwnershipContext';
import { cn } from '@/lib/utils';
import { useDevicePingOnLeagueEntry } from '@/hooks/useDevicePing';
import { NFLLeagueProvider, persistNflActiveLeagueId, useNFLLeague } from '@/contexts/NFLLeagueContext';
import NFLNav from '@/components/nfl/NFLNav';
import NFLHomePage from '@/components/nfl/NFLHomePage';
import NFLMyTeamPage from '@/components/nfl/NFLMyTeamPage';
import NFLMatchupsPage from '@/components/nfl/NFLMatchupsPage';
import NFLStandingsPage from '@/components/nfl/NFLStandingsPage';
import NFLSchedulePage from '@/components/nfl/NFLSchedulePage';
import NFLDraftPage from '@/components/nfl/NFLDraftPage';
import { NFLDraftRoomV2Content } from '@/pages/NFLDraftRoomV2';
import { roomForLeague } from '@/lib/nflDraftRoomChoice';
import { leagueIdFromSearch } from '@/lib/nflLeagueParam';
import NFLPlayersPage from '@/components/nfl/NFLPlayersPage';
import NFLPlayersMobile from '@/components/nfl/NFLPlayersMobile';
import NFLSabotagesPage from '@/components/nfl/NFLSabotagesPage';
import NFLSettingsPage from '@/components/nfl/NFLSettingsPage';
import NFLCreateLeagueWizard from '@/components/nfl/NFLCreateLeagueWizard';
import NFLJoinInline from '@/components/nfl/NFLJoinInline';

const NFLStagingContent = ({ roomVariant }: { roomVariant: RoomVariant }) => {
  const { currentLeague } = useNFLLeague();
  const [searchParams] = useSearchParams();
  // SAB-565 pt2: honor ?tab= so the wallet pages' shared nav can route into the
  // right shell tab (in-shell tab clicks still just call setActiveTab, no nav).
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'home');
  const isSmallViewport = useIsSmallViewport();
  // SAB-871: one mechanism for "is this a phone", shared with the rest of the app.
  const isMobile = useIsMobile();
  // SAB-884: declared BEFORE the early return so the hook order is stable. The V2 room owns
  // the screen on EVERY viewport, not just mobile -- the footer below it is what made the
  // desktop room's document 78px taller than the viewport too.
  const v2RoomOwnsScreen = activeTab === 'draft'
    && (roomVariant === 'legacy' ? false : roomForLeague(currentLeague) === 'new');
  useOwnsScreen(v2RoomOwnsScreen);
  const queryClient = useQueryClient();
  // SAB item 5: device-ping on contest entry. Keyed on currentLeague?.id inside the hook
  // (once per league per session); tab switches fire nothing; sends only if a recent
  // cached GPS fix exists; never prompts.
  useDevicePingOnLeagueEntry();

  // Item 4 — shell-level scroll save/restore, keyed by activeTab. Tabs unmount/remount on
  // switch (renderContent's switch returns a different component per tab) and tab clicks are
  // NOT route changes, so App.tsx's route-change scroll-reset never fires here — without this,
  // a tab inherits the prior tab's offset. Keep it in one place rather than per page. The
  // window is the scroller (min-h-screen content grows the document), matching App.tsx's reset.
  // SAB-873: rule (e) resolved ONCE, here, because TWO places now need the answer -- which
  // room renders, and whether it renders inside the shared shell. Keyed on the room CHOICE,
  // never on `activeTab === 'draft'`: the draft tab serves the LEGACY room too (an
  // in_progress or paused draft finishes where it started), and a tab-keyed exemption would
  // pull that room out of the shell as well. NFLDraftPage has no h-[100dvh] and no fixed
  // columns -- it is a normal flow page built for the 954px content column -- so taking it
  // full-bleed would break the one room that works today.
  // SAB-931: pinned at draft start, so a status transition or a deploy can no longer move
  // anyone between rooms mid-draft. See nflDraftRoomChoice for why this is a timestamp.
  const draftRoom = roomVariant === 'legacy' ? 'legacy' : roomForLeague(currentLeague);
  const v2RoomIsActive = activeTab === 'draft' && draftRoom === 'new';

  const scrollPositions = useRef<Record<string, number>>({});
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  // Route every tab change through here so the OUTGOING tab's scroll is saved first — covers
  // both the nav (onTabChange) and in-page programmatic navigation (onNavigate).
  const [tradeTargetTeamId, setTradeTargetTeamId] = useState<string | null>(null);
  const [tradeTargetPlayerId, setTradeTargetPlayerId] = useState<string | null>(null);


  const handleTabChange = useCallback((next: string) => {
    scrollPositions.current[activeTabRef.current] = window.scrollY;
    setActiveTab(next);
  }, []);
  /**
   * SAB-1062: ONE opener, shared by every surface that can show the player-detail modal.
   * Each of them passing its own navigation is how two surfaces come to disagree about
   * what "Trade" means -- and passing none is how the button came to do nothing at all.
   */
  const openTradeWith = useCallback((teamId: string, playerId?: string) => {
    setTradeTargetTeamId(teamId);
    setTradeTargetPlayerId(playerId ?? null);
    handleTabChange('trades');
  }, [handleTabChange]);

  // Restore the incoming tab's saved offset after its DOM commits (0 for a first visit).
  useLayoutEffect(() => {
    window.scrollTo(0, scrollPositions.current[activeTab] ?? 0);
  }, [activeTab]);

  // Freshness on tab return for context-owned records (Standings table, Home). The provider
  // mounts once, so its ['nfl-teams'] query can't refetch on tab return by itself — invalidate
  // it on every tab change (after the first mount, which just fetched). Page-owned queries
  // refetch on their own remount via staleTime:0. Structural sharing keeps this cheap.
  const didMountTabs = useRef(false);
  useEffect(() => {
    if (!didMountTabs.current) { didMountTabs.current = true; return; }
    queryClient.invalidateQueries({ queryKey: ['nfl-teams'] });
  }, [activeTab, queryClient]);

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return <NFLHomePage onNavigate={handleTabChange} />;
      case 'team':
        return <NFLMyTeamPage onNavigate={handleTabChange} initialTab="roster" onTradeWith={openTradeWith} />;
      case 'matchups':
        return <NFLMatchupsPage onNavigate={handleTabChange} onTradeWith={openTradeWith} />;
      case 'standings':
        return (
          <NFLStandingsPage
            // SAB-1046: a standings row's Trade button lands on the trade flow with that
            // team already selected. The id is held HERE rather than in a query param so a
            // back-navigation cannot resurrect a stale partner.
            // SAB-1068: the SHARED opener, not a second inline one. This used to null the
            // player id unconditionally, which was right when standings could only trade
            // at team level -- but the overlay now opens player detail, and its Trade
            // button carries a player. openTradeWith already treats a missing player id as
            // "no player", so the team-only case is unchanged.
            onTradeWith={openTradeWith}
          />
        );
      case 'schedule':
        return <NFLSchedulePage />;
      case 'draft':
        // Rule (e), decided above. 'legacy' pins the old room for /nfl/staging; 'auto'
        // decides per league from draft_status, so a draft that is IN FLIGHT finishes in the
        // room it started in even though /nfl is otherwise canonical.
        //
        // The active league is already resolved by NFLLeagueProvider above, and
        // handleNotificationTap sets it BEFORE navigating, so a push about league B is
        // judged on league B's status rather than whatever was active before the tap.
        //
        // Only the LEGACY room reaches this switch in practice -- the V2 room is rendered by
        // the full-height branch below, outside the shell. Kept exhaustive rather than
        // unreachable so this case still answers the question if that branch ever changes.
        return draftRoom === 'legacy' ? <NFLDraftPage /> : <NFLDraftRoomV2Content onExit={() => handleTabChange('home')} />;
      case 'players':
        return isSmallViewport ? <NFLPlayersMobile onTradeWith={openTradeWith} /> : <NFLPlayersPage onTradeWith={openTradeWith} />;
      case 'sabotages':
        return <NFLSabotagesPage />;
      case 'trades':
        // SAB-512 Phase 2: Trades folded into My Team. Keep the deep link working
        // (no dead route) by opening My Team on its Trades tab.
        return <NFLMyTeamPage onNavigate={handleTabChange} initialTab="trades" initialTradeTeamId={tradeTargetTeamId} initialTradePlayerId={tradeTargetPlayerId} />;
      case 'settings':
        return <NFLSettingsPage />;
      case 'wallet':
        // SAB-512 Phase 3: mobile More-sheet Wallet -> Settings funds section.
        return <NFLSettingsPage initialTab="funds" />;
      case 'create':
        return (
          <NFLCreateLeagueWizard
            // SAB-593: make the just-created league active before the reload, so its
            // OWN invite code and roster size show, not an arbitrary older league's.
            onComplete={(id) => { persistNflActiveLeagueId(id); setActiveTab('home'); window.location.reload(); }}
            onClose={() => setActiveTab('home')}
          />
        );
      case 'join':
        return (
          <NFLJoinInline
            // SAB-593: make the just-joined league active before the reload.
            onJoined={(id) => { persistNflActiveLeagueId(id); setActiveTab('home'); window.location.reload(); }}
            onBack={() => setActiveTab('home')}
          />
        );
      default:
        return <NFLHomePage onNavigate={handleTabChange} />;
    }
  };

  // SAB-873: the V2 draft room renders OUTSIDE the shared <main>.
  //
  // That <main> is `mx-auto max-w-4xl` + `overflow-x-hidden` -- a centred 954px reading
  // column (56rem at this app's 18px root, less px-6), correct for the other thirteen tabs
  // and wrong for a three-column app shell. The room needs 236 + centre + 322; inside 954
  // the centre track got ~380px and the board scrolled ~4.3 screens. That column is the
  // actual complaint, and no amount of min-w-0 widens it -- SAB-872 made the room correct
  // inside the box, this gives it the viewport.
  //
  // The shared <main> and every other tab's markup are UNTOUCHED: this is a separate branch
  // taken before it, not an edit to it. A className change there would have been a change to
  // all fourteen tabs at once.
  //
  // Full-height flex rather than `h-[calc(100dvh - <nav>)]`: NFLNav is `sticky top-0` and so
  // occupies 3.5rem + 1px of flow, and hard-coding that number here would silently desync
  // the moment the nav's height changes. Letting flex do the arithmetic cannot desync.
  // (The mobile bottom nav is `fixed`, so it costs no flow height -- it overlays the room's
  // bottom edge on small viewports, which is pre-existing and is SAB-871's problem.)
  if (v2RoomIsActive) {
    return (
      <div className="h-[100dvh] overflow-hidden flex flex-col bg-gradient-dark">
        {/*
          SAB-871: the app's bottom bar is suppressed ONLY when the V2 room is what is
          rendering AND we are on a phone -- i.e. only when MobileDraftRoom is there to
          provide a replacement bar.

          ROOM-CHOICE-KEYED, NOT TAB-KEYED. `v2RoomIsActive` already encodes rule (e), so the
          LEGACY room -- which serves in_progress/paused drafts and has no tab bar of its own
          -- keeps the app nav. Keying this on `activeTab === 'draft'` would strand a mobile
          user in a live draft with no navigation at all.
        */}
        {/*
          SAB-884: on a phone the room is the WHOLE screen -- no app top bar above it, no
          site footer below it, nothing between the status bar and the home indicator.

          The top bar was kept in SAB-871 as a secondary way out, because at that point the
          room had no exit of its own. It does now (MobileDraftRoom's header control, a
          required prop), so the bar is no longer load-bearing and its logo/switcher/
          hamburger were simply pushing the room down and out of the viewport.

          On DESKTOP the bar stays: that room has no tab bar of its own, so the app nav is
          still the navigation. Only the footer is suppressed there, via the screen claim
          above.
        */}
        {!isMobile && (
          <NFLNav
            activeTab={activeTab}
            onTabChange={handleTabChange}
            hideBottomNav={false}
          />
        )}
        {/*
          SAB-875: clearance for the app's mobile bottom bar -- but ONLY when the DESKTOP
          room is the one rendering.

          NFLNav's bottom bar is `sm:hidden fixed bottom-0`, so it exists below 640px and,
          being fixed, costs no flow height: it simply covers the room's bottom edge (the
          board legend). The shared <main> every other tab uses carries
          `pb-[calc(env(safe-area-inset-bottom)+80px)]` for exactly this; SAB-873 moved this
          room out of that <main> and left the compensation behind.

          GATED ON !isMobile, not on `sm:` alone. Since SAB-871, below 640 on a TOUCH device
          MobileDraftRoom renders and owns its own bar -- `hideBottomNav` above has already
          removed the app one. Padding there would add ~114px of dead space beneath a room
          that needs none. What is left is the band this actually protects: a NON-TOUCH
          browser under 640px -- a laptop or desktop with the window dragged narrow, or split
          screen -- where `useIsMobile` is false, the desktop room renders, and the app bar
          appears anyway. Measured: 500 and 620 non-touch land there, 700 does not.

          On the flex-1 CHILD, never the h-[100dvh] parent: padding on the parent adds to a
          height that is already exactly the viewport and brings back the page scroll SAB-873
          removed. Here it only shrinks the content box.
        */}
        <div
          className={cn(
            'flex-1 min-h-0',
            !isMobile && 'pb-[calc(env(safe-area-inset-bottom)+80px)] sm:pb-0',
          )}
        >
          <NFLDraftRoomV2Content onExit={() => handleTabChange('home')} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark overflow-x-hidden overflow-y-auto w-full max-w-[100vw]">
      {/* Top bar with tab strip + mobile bottom nav */}
      <NFLNav activeTab={activeTab === 'trades' ? 'team' : activeTab} onTabChange={handleTabChange} />

      {/* Full-width content below top nav */}
      <main className="w-full px-4 sm:px-6 py-6 pb-[calc(env(safe-area-inset-bottom)+80px)] sm:pb-6 mx-auto max-w-4xl box-border overflow-x-hidden">
        {renderContent()}
        {/* SAB-512 Phase 5: readable sportradar mark (matches MarketingFooter h-6 opacity-85). */}
        <img src="/badges/sportradar-powered-by.png" alt="Powered by Sportradar" className="h-6 w-auto opacity-85 mx-auto mt-8" />
      </main>
    </div>
  );
};

/**
 * Routing cutover.
 *
 *   'auto'   -- /nfl, the canonical path. The draft tab picks its room per league by
 *               draft_status (rule (e), see nflDraftRoomChoice).
 *   'legacy' -- /nfl/staging, the transition path. ALWAYS the old room, whatever the
 *               status. This is the rollback surface and the destination of links already
 *               out in the world (sent emails, delivered pushes, bookmarks), so it must
 *               stay predictable rather than tracking the new rule.
 */
export type RoomVariant = 'auto' | 'legacy';

const NFLStaging = ({ roomVariant = 'auto' }: { roomVariant?: RoomVariant }) => {
  // ?league=<id> -- applied BEFORE NFLLeagueProvider mounts, which is the entire point.
  //
  // A useState initialiser, not an effect: it runs during THIS component's first render,
  // and React renders a parent before its children, so the id is in storage before the
  // provider below reads it. An effect would fire after the provider had already resolved
  // a league and after the draft tab had judged rule (e) from that league's status -- the
  // wrong league's room, then a correction, which is the flicker-and-wrong-data path the
  // push flow avoids by setting the league before it navigates.
  //
  // The provider still decides whether to honour the id: resolveActiveNflLeagueId accepts a
  // cached id only for a league the user is a member of. This writes a request, not a grant.
  useState(() => {
    const requested = leagueIdFromSearch(window.location.search);
    if (requested) persistNflActiveLeagueId(requested);
    return requested;
  });

  return (
    <NFLLeagueProvider>
      <NFLStagingContent roomVariant={roomVariant} />
    </NFLLeagueProvider>
  );
};

export default NFLStaging;
